import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Package, ArrowsOutCardinal, CaretRight } from "@phosphor-icons/react";
import MoveToDialog from "./MoveToDialog";

export function MoveButton({ asset, onMoved }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button data-testid="asset-move-to" onClick={() => setOpen(true)}
        className="h-8 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 font-semibold text-xs">
        <ArrowsOutCardinal size={14} weight="bold" className="mr-1" />Move to…
      </Button>
      {open && <MoveToDialog asset={asset} open={open} onOpenChange={setOpen} onMoved={onMoved} />}
    </>
  );
}

export function Breadcrumbs({ asset }) {
  const chain = asset?.effective_location?.chain || []; // immediate parent first
  if (!asset?.contained_in && chain.length === 0) return null;
  const ancestors = [...chain].reverse(); // top -> down
  return (
    <div data-testid="asset-breadcrumbs" className="flex items-center flex-wrap gap-1 text-sm text-slate-500">
      {asset.effective_location?.location_name && <span className="font-semibold text-slate-600">{asset.effective_location.location_name}</span>}
      {ancestors.map((c) => (
        <span key={c.id} className="flex items-center gap-1">
          <CaretRight size={12} className="text-slate-300" />
          <Link to={`/assets/${c.id}`} className="hover:text-blue-600">{c.asset_number} {c.name}</Link>
        </span>
      ))}
      <CaretRight size={12} className="text-slate-300" />
      <span className="font-semibold text-slate-800">{asset.asset_number} {asset.name}</span>
    </div>
  );
}

export function ItemsWithin({ assetId, onChanged }) {
  const [kids, setKids] = useState(null);
  const load = useCallback(() => api.get(`/assets/${assetId}/children`).then((r) => setKids(r.data)).catch(() => setKids([])), [assetId]);
  useEffect(() => { load(); }, [load]);
  if (!kids || kids.length === 0) return null;
  return (
    <div data-testid="items-within" className="bg-white border-2 border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Package size={18} weight="bold" className="text-slate-600" />
        <h2 className="font-display text-lg font-black text-slate-900">Items Within</h2>
        <span className="ml-auto font-mono text-xs text-slate-400">{kids.length}</span>
      </div>
      <div className="flex flex-col gap-1.5">
        {kids.map((k) => (
          <Link key={k.id} to={`/assets/${k.id}`} data-testid={`within-${k.id}`}
            className="flex items-center gap-2 border border-slate-200 rounded px-3 py-2 hover:bg-slate-50">
            <Package size={16} className="text-slate-400 shrink-0" />
            <span className="min-w-0">
              <span className="font-mono text-xs text-slate-500">{k.asset_number}</span>{" "}
              <span className="font-semibold text-slate-800">{k.name}</span>
              {k.child_count > 0 && <span className="text-xs text-slate-400"> · contains {k.child_count}</span>}
            </span>
            {k.oos && <span className="text-[10px] font-bold uppercase bg-red-100 text-red-700 rounded px-1.5">OOS</span>}
            <CaretRight size={14} className="text-slate-300 ml-auto" />
          </Link>
        ))}
      </div>
    </div>
  );
}
